/**
 * VaultGate (Task #84) — top-level wrapper that ensures every authenticated
 * user has a vault and that it's unlocked for this session before they reach
 * the rest of the app. Cheap to render once the vault is in memory.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchVault } from "@/lib/vault/vaultClient";
import { useVaultSession } from "@/lib/vault/vaultState";
import VaultCreateModal from "./VaultCreateModal";
import VaultUnlockModal from "./VaultUnlockModal";
import VaultActivityWatcher from "./VaultActivityWatcher";
import VaultIdlePrompt from "./VaultIdlePrompt";

interface Props {
  children: React.ReactNode;
}

export default function VaultGate({ children }: Props) {
  const session = useVaultSession();
  // We only need to know whether a vault row exists. The blobs themselves are
  // fetched again by unlockWithPassword / unlockWithRecoveryPhrase.
  // null = confirmed no vault yet; ServerVault = exists.
  const { data: vault, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["/api/vault", "exists"],
    queryFn: fetchVault,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const [justCreated, setJustCreated] = useState(false);

  useEffect(() => {
    if (justCreated) refetch();
  }, [justCreated, refetch]);

  // Loading — block app content. We MUST NOT show children before we know the
  // vault state, otherwise a transient flash could let an unauthenticated UI
  // moment leak; we also must not render a create modal before we've confirmed
  // there really is no vault (that would overwrite an existing one).
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading your vault…</p>
      </div>
    );
  }

  // Network/server error — show retry, NEVER fall through to create modal,
  // because that path would overwrite an existing vault on the next save.
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 gap-3 text-center max-w-md mx-auto">
        <AlertTriangle className="w-6 h-6 text-destructive" />
        <p className="text-sm font-medium">Couldn't reach your vault.</p>
        <p className="text-xs text-muted-foreground">
          ForgeRestore couldn't confirm whether your encryption vault exists yet.
          Please check your connection and retry — we won't show the rest of the
          app until we know.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="button-vault-retry"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  // Confirmed no vault → force creation. Only reached after a successful
  // fetch that returned null (the queryFn translates 404 → null).
  if (vault === null) {
    return <VaultCreateModal open={true} onCreated={() => setJustCreated(true)} />;
  }

  // Vault exists but locked on this device → force unlock.
  if (!session) {
    return <VaultUnlockModal open={true} onUnlocked={() => refetch()} />;
  }

  return (
    <>
      <VaultActivityWatcher />
      <VaultIdlePrompt />
      {children}
    </>
  );
}
